import type { CameraAnglesRef, HotspotType, Position3D } from "@/types";
import React from "react";
import { createContext } from "use-context-selector";

interface ViewerPlacementValue {
  placementMode: HotspotType | null;
  setPlacementMode: React.Dispatch<React.SetStateAction<HotspotType | null>>;
}

interface ViewerDrawingValue {
  isDrawing: boolean;
  drawingType: "Area" | "Video" | "Mask" | null;
  setDrawingType: React.Dispatch<
    React.SetStateAction<"Area" | "Video" | "Mask" | null>
  >;
  drawPoints: Position3D[];
  setDrawPoints: React.Dispatch<React.SetStateAction<Position3D[]>>;
  /** id of the Area hotspot whose shape is being re-edited (null = drawing a new one). */
  drawEditingId: string | null;
  setDrawEditingId: React.Dispatch<React.SetStateAction<string | null>>;
}

interface ViewerHotspotFocusValue {
  focusedHsId: string | null;
  setFocusedHsId: (id: string | null) => void;
  movingHsId: string | null;
  setmovingHsId: (id: string | null) => void;
}

export type CaptureMode = "thumbnail" | "initial-position" | "photo" | null;

export type SphereCorrectionMode = "roll" | "pitch" | "northOffset" | "yaw";

export interface SphereCorrectionState {
  initialRoll: number;
  initialPitch: number;
  initialNorthOffset: number;
  initialYaw: number;
  mode: SphereCorrectionMode;
  onConfirm: (roll: number, pitch: number, northOffset: number) => Promise<void>;
  /** For yaw mode: save sphere_yaw + auto-set north_offset=0 */
  onConfirmYaw?: (yaw: number) => Promise<void>;
  /** Apply roll+pitch to every scene in the tour (roll/pitch modes only) */
  onConfirmAll?: (roll: number, pitch: number) => Promise<void>;
  onCancel: () => void;
}

interface AmbientAudioValue {
  isPlaying: boolean;
  volume: number;
  progress: number;
  duration: number;
  hasAudio: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  toggle: () => void;
  setVolume: (vol: number) => void;
  seek: (time: number) => void;
  /** Playlist length (1 = single ambient track). */
  trackCount: number;
  /** Index of the currently playing track. */
  trackIndex: number;
  next: () => void;
  prev: () => void;
}

interface ViewerUIValue {
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  autoPlay: boolean;
  setAutoPlay: React.Dispatch<React.SetStateAction<boolean>>;
  hudVisible: boolean;
  setHudVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isMinimizedMap: boolean;
  setIsMinimizedMap: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFloorId: string | null;
  handleSetSelectedFloorId: React.Dispatch<React.SetStateAction<string | null>>;
  focusedRoomId: string | null;
  setFocusedRoomId: React.Dispatch<React.SetStateAction<string | null>>;
  hoveredRoomId: string | null;
  setHoveredRoomId: React.Dispatch<React.SetStateAction<string | null>>;
  captureMode: CaptureMode;
  setCaptureMode: React.Dispatch<React.SetStateAction<CaptureMode>>;
  sphereCorrection: SphereCorrectionState | null;
  setSphereCorrection: React.Dispatch<React.SetStateAction<SphereCorrectionState | null>>;
  isSceneBrowserVisible: boolean;
  setIsSceneBrowserVisible: React.Dispatch<React.SetStateAction<boolean>>;
  cameraAnglesRef: CameraAnglesRef;
}

export const ViewerUIContext = createContext<ViewerUIValue | null>(null);
export const ViewerPlacementContext =
  createContext<ViewerPlacementValue | null>(null);
export const ViewerHotspotFocusContext =
  createContext<ViewerHotspotFocusValue | null>(null);
export const ViewerDrawingContext = createContext<ViewerDrawingValue | null>(
  null,
);
export const AmbientAudioContext = createContext<AmbientAudioValue | null>(
  null,
);

export type {
  ViewerPlacementValue,
  ViewerDrawingValue,
  ViewerHotspotFocusValue,
  ViewerUIValue,
  AmbientAudioValue,
};
